/** @file consumeResetFlag: `?reset=layout|all` clears the stored UI state, then strips the flag from the URL. */
import { EDITOR_SETTINGS } from './editorSettings';

/** What `consumeResetFlag` reads: the page location, the history to rewrite and the storage to clear. */
export type ResetEnv = {
  location: Pick<Location, 'search' | 'pathname' | 'hash'>;
  history: Pick<History, 'replaceState'>;
  storage: Pick<Storage, 'length' | 'key' | 'removeItem'>;
};

const PREFIX = 'pose3d.';
const LAYOUT_PREFIX = 'pose3d.layout';

/** The stored keys that start with `prefix`. */
function keysWith(storage: ResetEnv['storage'], prefix: string): string[] {
  const keys: string[] = [];
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i);
    if (key?.startsWith(prefix) === true) keys.push(key);
  }
  return keys;
}

/** `layout`: collapsed panes and the last route; `all`: every `pose3d.*` setting too. */
function clear(kind: 'layout' | 'all', storage: ResetEnv['storage']): void {
  const keys = kind === 'all' ? keysWith(storage, PREFIX) : keysWith(storage, LAYOUT_PREFIX);
  for (const key of keys) storage.removeItem(key);
  if (kind === 'layout') EDITOR_SETTINGS.lastRoute.save(null);
}

/**
 * Run once at start, before any setting is read. Returns the kind it cleared (null when the URL has no valid
 * `reset` flag); an unknown value is left alone. The flag is removed from the URL either way it was recognised.
 */
export function consumeResetFlag(env?: ResetEnv): 'layout' | 'all' | null {
  const e = env ?? { location: globalThis.location, history: globalThis.history, storage: globalThis.localStorage };
  const params = new URLSearchParams(e.location.search);
  const kind = params.get('reset');
  if (kind !== 'layout' && kind !== 'all') return null;
  try {
    clear(kind, e.storage);
  } catch {
    // Storage blocked: nothing persisted, nothing to clear.
  }
  params.delete('reset');
  const rest = params.toString();
  e.history.replaceState(null, '', `${e.location.pathname}${rest === '' ? '' : `?${rest}`}${e.location.hash}`);
  return kind;
}
